// Team / Users — an admin lists who has access to the company, invites colleagues, and removes
// access. Access is exactly THREE levels and is NOT editable (owner directive 2026-09-11): OWNER,
// ADMIN, USER. An invited member is ALWAYS a USER — they capture and submit documents for an admin
// to approve; owners stay owners and admins stay admins. This is what lets a "normal user" exist:
// before, every signup was granted `owner` (admin) so everyone could finalise/approve (issues
// #60/#61). Admin-only, matching the API gate (document_approvals_service._is_company_admin).
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { tenantShell } from './tshell.js';
import { t } from '../shared/i18n.js';
import { companyRole, isCompanyAdmin } from '../shared/companyrole.js';
import { rowDelete } from '../shared/confirm.js';
import { formatDate } from './bank_helpers.js';

// The three visible access levels → their badge label + class.
const ACCESS = {
  owner: ['team.access_owner', 'Owner', 'role-owner'],
  admin: ['team.access_admin', 'Admin', 'role-admin'],
  user: ['team.access_user', 'User', 'role-member'],
};

export function teamScreen(companyId){
  const rows = signal([]), loading = signal(true), err = signal('');
  const coRole = companyRole(companyId);

  // invite form
  const newOpen = signal(false), saving = signal(false), formErr = signal('');
  const nEmail = signal(''), nFirst = signal(''), nLast = signal('');
  // A returned set-password link (SMTP off) the admin must pass on — persistent, never auto-dismissed.
  const inviteLink = signal(null);   // { email, link } | null
  const linkCopied = signal(false);

  // per-row remove state
  const armedId = signal(null), removing = signal(false), removeErr = signal('');

  async function load(){
    loading.value = true;
    try {
      const r = await api.get('/companies/' + companyId + '/members');
      rows.value = r.data || [];
      err.value = '';
    } catch(e){
      rows.value = [];
      err.value = (e.data && e.data.error) || t('team.err_load', 'Could not load the team.');
    }
    loading.value = false;
  }
  load();

  function openNew(){
    formErr.value = ''; inviteLink.value = null; newOpen.value = !newOpen.value;
    if(newOpen.value) queueMicrotask(() => document.querySelector('.team-new input:not([type="checkbox"])')?.focus());
  }
  const clearFormErr = () => { if(formErr.value) formErr.value = ''; };

  async function addMember(e){
    e.preventDefault();
    const email = nEmail.value.trim();
    if(!email || !email.includes('@')){ formErr.value = t('team.err_email', 'Enter a valid email address.'); return; }
    saving.value = true; formErr.value = '';
    try {
      const res = await api.post('/companies/' + companyId + '/members', {
        email, first_name: nFirst.value.trim(), last_name: nLast.value.trim(),
      });
      if(res && res.error){ formErr.value = res.error; saving.value = false; return; }
      if(res && res.set_password_link){ inviteLink.value = { email: res.email || email, link: res.set_password_link }; linkCopied.value = false; }
      nEmail.value = ''; nFirst.value = ''; nLast.value = '';
      newOpen.value = false; saving.value = false;
      await load();
    } catch(err2){
      formErr.value = (err2.data && err2.data.error) || err2.message || t('team.err_add', 'Could not add that member.');
      saving.value = false;
    }
  }

  function armRemove(id){ removeErr.value = ''; armedId.value = id; }
  function cancelRemove(){ armedId.value = null; removeErr.value = ''; }
  async function doRemove(r){
    removing.value = true; removeErr.value = '';
    try {
      const res = await api.post('/companies/' + companyId + '/members/' + r.user_id + '/remove', {});
      if(res && res.error){ removeErr.value = res.error; removing.value = false; return; }
      armedId.value = null; removing.value = false;
      await load();
    } catch(e){
      removeErr.value = (e.data && e.data.error) || e.message || t('team.err_remove', 'Could not remove that member.');
      removing.value = false;
    }
  }

  async function copyLink(){
    try { await navigator.clipboard.writeText(inviteLink.value.link); linkCopied.value = true; }
    catch { linkCopied.value = false; }
  }

  const whoName = r => r.display_name || (r.email || '').split('@')[0];
  const accessBadge = (a) => {
    const cfg = ACCESS[a] || ACCESS.user;
    return html`<span class=${'role-badge ' + cfg[2]}>${t(cfg[0], cfg[1])}</span>`;
  };

  // The members table — all signals read at the top; plain ${} inside (no nested reactive slots).
  const list = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const data = rows.value, armed = armedId.value, rmBusy = removing.value, rmErr = removeErr.value;
    if(!data.length) return html`<div class="empty">${t('team.none', 'No members yet.')}</div>`;
    return html`<div class="tbl-wrap"><table class="list team-tbl">
      <thead><tr>
        <th>${t('team.col_member', 'Member')}</th>
        <th>${t('team.col_access', 'Access')}</th>
        <th>${t('team.col_added', 'Added')}</th>
        <th class="ta-r">${t('team.col_action', '')}</th>
      </tr></thead>
      <tbody>${data.map(r => html`<tr>
          <td>
            <div class="team-who"><b>${whoName(r)}</b>${r.is_self ? html` <span class="team-you">${t('team.you', 'you')}</span>` : ''}</div>
            <div class="team-email muted">${r.email}${r.is_active ? '' : html` · <span class="pill pill-void">${t('team.disabled', 'disabled')}</span>`}</div>
          </td>
          <td>${accessBadge(r.access)}</td>
          <td class="mono muted">${r.granted_at ? formatDate(r.granted_at, 'yyyy-MM-dd') : '—'}</td>
          <td class="ta-r">
            ${r.is_owner
              ? html`<span class="muted team-locked" title=${t('team.owner_locked', "The owner can't be removed.")}>—</span>`
              : rowDelete({ armed: armed === r.user_id, busy: rmBusy,
                  error: armed === r.user_id ? rmErr : '',
                  onArm: () => armRemove(r.user_id), onCancel: cancelRemove, onDelete: () => doRemove(r),
                  label: t('team.remove', 'Remove') })}
          </td>
        </tr>`)}</tbody>
    </table></div>`;
  };

  const content = html`<div class="page page-wide">
    <div class="page-head">
      <div><span class="eyebrow">${() => t('team.eyebrow', 'Company access')}</span><h1>${() => t('team.title', 'Team')}</h1>
        <p class="muted">${() => t('team.subtitle', 'Who can use this company. Owners and admins have full access; invited users capture and submit documents for an admin to approve.')}</p></div>
      <div class="row-actions">
        ${() => (coRole.value && !isCompanyAdmin(coRole.value))
          ? ''
          : html`<button class="btn primary" @click=${openNew}>+ ${() => t('team.add', 'Invite user')}</button>`}
      </div>
    </div>

    ${() => (coRole.value && !isCompanyAdmin(coRole.value))
      ? html`<div class="muted-block">${t('team.needs_admin', 'Only an admin can manage the team.')}</div>`
      : ''}

    ${() => inviteLink.value ? html`<div class="card mb-2 team-link-card">
      <div class="card-body">
        <div class="team-link-head">✓ ${t('team.invited', 'Invited')} <b>${inviteLink.value.email}</b></div>
        <p class="muted">${t('team.link_hint', 'Email is not set up on this environment, so send them this link to set their password (valid 7 days):')}</p>
        <div class="team-link-row">
          <input class="form-control team-link-input" readonly .value=${inviteLink.value.link} @focus=${e => e.target.select()} aria-label=${t('team.set_pw_link', 'Set-password link')}>
          <button type="button" class="btn" @click=${copyLink}>${() => linkCopied.value ? t('team.copied', 'Copied') : t('common.copy', 'Copy')}</button>
        </div>
        <div class="team-link-actions"><button type="button" class="btn btn-outline sm" @click=${() => { inviteLink.value = null; }}>${t('common.dismiss', 'Dismiss')}</button></div>
      </div>
    </div>` : ''}

    ${() => newOpen.value ? html`<form class="card mb-2 team-new" @submit=${addMember}>
      <div class="card-body">
        <div class="team-new-grid">
          <input class="form-control" type="email" .value=${nEmail} @input=${e => { clearFormErr(); nEmail.value = e.target.value; }} placeholder=${t('team.email_ph', 'Email e.g. jane@acme.co.za')} aria-label=${t('common.email', 'Email')}>
          <input class="form-control" .value=${nFirst} @input=${e => { nFirst.value = e.target.value; }} placeholder=${t('team.first_ph', 'First name e.g. Jane')} aria-label=${t('team.first_name', 'First name')}>
          <input class="form-control" .value=${nLast} @input=${e => { nLast.value = e.target.value; }} placeholder=${t('team.last_ph', 'Last name e.g. Doe')} aria-label=${t('team.last_name', 'Last name')}>
        </div>
        <small class="team-new-hint">${t('team.invite_hint', 'They join as a User — they can capture and submit documents for an admin to approve, but cannot finalise, approve or reject.')}</small>
        <div class="ct-new-actions">
          ${() => formErr.value ? html`<div class="row-err">${formErr}</div>` : ''}
          <span class="jrn-spacer"></span>
          <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? t('team.adding', 'Inviting…') : t('team.add', 'Invite user')}</button>
          <button type="button" class="btn btn-outline" @click=${() => { newOpen.value = false; }}>${t('common.cancel', 'Cancel')}</button>
        </div>
      </div>
    </form>` : ''}

    ${list}
  </div>`;

  return tenantShell(companyId, t('team.title', 'Team'), content);
}
